/**
 * MobileNavBar — 5-tab bottom navigation bar.
 *
 * Always-visible labels (not icon-only). The retirement planning audience
 * uses the app infrequently and won't memorise icon meanings.
 *
 * Tabs: Home, Tools, Plans, Projection, Account
 * Projection tab is disabled when no projection exists — tapping it shows a toast.
 */

import { memo, type MouseEvent } from 'react';
import { NavLink } from 'react-router-dom';
import { NAV_LABELS } from '../../constants/wizardSteps';
import './MobileNavBar.css';

// SVG icons extracted as module constants to avoid re-allocation on every render
const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const WrenchIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const ChartIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const PersonIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

interface MobileNavBarProps {
  /** Whether a projection exists — controls Projection tab disabled state */
  hasProjection: boolean;
  /** Called when the disabled Projection tab is tapped */
  onResultsDisabledTap: () => void;
}

export const MobileNavBar = memo(function MobileNavBar({ hasProjection, onResultsDisabledTap }: Readonly<MobileNavBarProps>) {
  const handleResultsTap = (e: MouseEvent) => {
    if (!hasProjection) {
      e.preventDefault();
      onResultsDisabledTap();
    }
  };

  return (
    <nav className="mobile-navbar" role="navigation" aria-label="Main navigation">
      <NavLink to="/" end className="mobile-navbar__tab" aria-label={NAV_LABELS.HOME}>
        <HomeIcon />
        <span className="mobile-navbar__label">{NAV_LABELS.HOME}</span>
      </NavLink>

      <NavLink to="/tools" className="mobile-navbar__tab" aria-label={NAV_LABELS.TOOLS}>
        <WrenchIcon />
        <span className="mobile-navbar__label">{NAV_LABELS.TOOLS}</span>
      </NavLink>

      <NavLink to="/plan" className="mobile-navbar__tab" aria-label={NAV_LABELS.PLANS}>
        <DocumentIcon />
        <span className="mobile-navbar__label">{NAV_LABELS.PLANS}</span>
      </NavLink>

      <NavLink
        to={hasProjection ? '/projections/compare' : ''}
        className={`mobile-navbar__tab${hasProjection ? '' : ' mobile-navbar__tab--disabled'}`}
        aria-label={NAV_LABELS.PROJECTION}
        aria-disabled={!hasProjection}
        onClick={handleResultsTap}
      >
        <ChartIcon />
        <span className="mobile-navbar__label">{NAV_LABELS.PROJECTION}</span>
      </NavLink>

      <NavLink to="/account" className="mobile-navbar__tab" aria-label={NAV_LABELS.ACCOUNT}>
        <PersonIcon />
        <span className="mobile-navbar__label">{NAV_LABELS.ACCOUNT}</span>
      </NavLink>
    </nav>
  );
});
